import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Modal, Button, Form, Checkbox, Message, Header, Grid, Input } from 'semantic-ui-react'
import TrillDescription from 'trill-description'

import { buildEmbedHtml, safeSandbox, resolveEmbedDimensions, DEFAULTS } from './embedSerializer'
import { validateDimensions, filterDimensionErrors, canSubmit as canSubmitForm } from './validation'
import { SAMPLE_HTML, SAMPLE_PLACEHOLDER } from './sample'
import { HtmlEmbedGuide, getGuideSummary, SUPPORTED_LANGS, LANG_LABELS, DEFAULT_LANG } from './guide'
import formatHtml from './formatHtml'

const { TrillHtmlIframeEmbed } = TrillDescription
const AllowList = TrillHtmlIframeEmbed.AllowList

const PREVIEW_DEBOUNCE_MS = 300
const PREVIEW_FRAME_HEIGHT_PX = 450

function resolveDialogPreviewIframeStyle(width, height, allowList) {
  const dims = resolveEmbedDimensions({
    width,
    height,
    maxWidth: allowList.maxWidth,
    maxHeight: allowList.maxHeight,
  })
  if (dims.mode === 'aspect') {
    return {
      width: `${dims.width}px`,
      maxWidth: '100%',
      aspectRatio: `${dims.width} / ${dims.height}`,
      height: 'auto',
      border: 'none',
      display: 'block',
    }
  }
  return {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  }
}

function FieldError({ message }) {
  if (!message) return null
  return (
    <div
      style={{
        color: '#9f3a38',
        fontSize: 12,
        marginTop: 4,
      }}
    >
      {message}
    </div>
  )
}

FieldError.propTypes = {
  message: PropTypes.string,
}

FieldError.defaultProps = {
  message: null,
}

const REQUIRED_SANDBOX_TOKEN = 'allow-scripts'
const SANDBOX_TOKENS = ['allow-scripts', 'allow-popups', 'allow-forms', 'allow-modals']

function SandboxCheckbox({ token, required, checked, onChange }) {
  return (
    <Checkbox
      label={required ? `${token} (必須)` : token}
      checked={required || checked}
      onChange={required ? undefined : onChange}
      readOnly={required}
      style={{ marginRight: 16 }}
    />
  )
}

SandboxCheckbox.propTypes = {
  token: PropTypes.string.isRequired,
  required: PropTypes.bool,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
}

SandboxCheckbox.defaultProps = {
  required: false,
}

const propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialHtml: PropTypes.string,
  initialWidth: PropTypes.number,
  initialHeight: PropTypes.number,
  initialSandbox: PropTypes.string,
}

const defaultProps = {
  open: false,
  initialHtml: '',
  initialWidth: DEFAULTS.width,
  initialHeight: DEFAULTS.height,
  initialSandbox: DEFAULTS.sandbox,
}

class HtmlEmbedDialog extends Component {
  constructor(props) {
    super(props)
    this.allowList = new AllowList()
    this.state = this.buildInitialState(props)
    this.validatePreview = _.debounce(this.validatePreview.bind(this), PREVIEW_DEBOUNCE_MS)
  }

  componentDidMount() {
    if (this.props.open) this.setState({ isValidating: true }, this.validatePreview)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState({ ...this.buildInitialState(this.props), isValidating: true }, this.validatePreview)
    }
  }

  buildInitialState(props) {
    const sandboxTokens = safeSandbox(props.initialSandbox)
      .split(/\s+/)
      .filter(Boolean)
    return {
      html: formatHtml(props.initialHtml || ''),
      width: props.initialWidth ? String(props.initialWidth) : '',
      height: props.initialHeight ? String(props.initialHeight) : '',
      sandbox: new Set([REQUIRED_SANDBOX_TOKEN, ...sandboxTokens]),
      errors: [],
      warnings: [],
      previewSrcdoc: '',
      isValidating: !!props.open,
      guideLang: DEFAULT_LANG,
    }
  }

  handleGuideLangChange = lang => () => {
    this.setState({ guideLang: lang })
  }

  validatePreview() {
    const { html, width, height } = this.state

    TrillHtmlIframeEmbed.parse(html || '', {
      width: parseInt(width, 10) || 0,
      height: parseInt(height, 10) || 0,
      validateAll: true,
    })
      .then(embed => {
        this.setState({
          errors: embed.errors,
          warnings: embed.warnings,
          previewSrcdoc: this.buildPreviewSrcdoc(embed.sanitizedHtml()),
          isValidating: false,
        })
      })
      .catch(err => {
        this.setState({
          errors: [err.message || String(err)],
          warnings: [],
          previewSrcdoc: '',
          isValidating: false,
        })
      })
  }

  buildPreviewSrcdoc(inner) {
    return [
      '<!doctype html><html><head><meta charset="utf-8">',
      '<style>body{margin:0;padding:8px;font-family:sans-serif;}</style>',
      '</head><body>',
      inner,
      '</body></html>',
    ].join('')
  }

  handleHtmlChange = (event, data) => {
    const value = data ? data.value : event.target.value
    this.setState({ html: value, isValidating: true }, this.validatePreview)
  }

  handleSizeChange = key => (event, data) => {
    const raw = data ? data.value : event.target.value
    this.setState({ [key]: raw, isValidating: true }, this.validatePreview)
  }

  handleSandboxToggle = flag => () => {
    if (flag === REQUIRED_SANDBOX_TOKEN) return
    const sandbox = new Set(this.state.sandbox)
    if (sandbox.has(flag)) sandbox.delete(flag)
    else sandbox.add(flag)
    this.setState({ sandbox })
  }

  handleLoadSample = () => {
    this.setState({ html: SAMPLE_HTML, isValidating: true }, this.validatePreview)
  }

  handleFormatHtml = () => {
    const formatted = formatHtml(this.state.html)
    if (formatted === this.state.html) return
    this.setState({ html: formatted, isValidating: true }, this.validatePreview)
  }

  handleInsert = () => {
    const { html, width, height, sandbox } = this.state
    const figureHtml = buildEmbedHtml({
      html,
      width: parseInt(width, 10) || 0,
      height: parseInt(height, 10) || 0,
      sandbox: Array.from(sandbox).join(' '),
    })
    this.props.onSubmit(figureHtml)
    this.props.onClose()
  }

  render() {
    const { open, onClose, initialHtml } = this.props
    const { html, width, height, sandbox, errors, warnings, previewSrcdoc, isValidating, guideLang } = this.state

    const fieldErrors = validateDimensions({ width, height }, this.allowList)
    const summaryErrors = filterDimensionErrors(errors)
    const submitEnabled = canSubmitForm({ html, fieldErrors, summaryErrors, isValidating })
    const sandboxAttr = safeSandbox(Array.from(sandbox).join(' '))

    return (
      <Modal open={open} onClose={onClose} size="fullscreen" closeIcon>
        <Modal.Header>HTML埋め込み</Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Grid columns={2} stackable>
              <Grid.Column>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: '0.14285714rem',
                  }}
                >
                  <Header sub style={{ margin: 0 }}>
                    HTML
                  </Header>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      type="button"
                      onClick={this.handleFormatHtml}
                      disabled={!html}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: html ? '#4183c4' : '#bbb',
                        cursor: html ? 'pointer' : 'not-allowed',
                        fontSize: 12,
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      コードを整形
                    </button>
                    <button
                      type="button"
                      onClick={this.handleLoadSample}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#4183c4',
                        cursor: 'pointer',
                        fontSize: 12,
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      サンプルを挿入
                    </button>
                  </div>
                </div>
                <Form.TextArea
                  rows={28}
                  value={html}
                  onChange={this.handleHtmlChange}
                  placeholder={SAMPLE_PLACEHOLDER}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
              </Grid.Column>
              <Grid.Column>
                <Header sub>プレビュー</Header>
                <div
                  style={{
                    height: PREVIEW_FRAME_HEIGHT_PX,
                    overflow: 'auto',
                    border: '1px solid #ccc',
                    background: '#fff',
                  }}
                >
                  <iframe
                    title="html-embed-preview"
                    srcDoc={previewSrcdoc}
                    sandbox={sandboxAttr}
                    style={resolveDialogPreviewIframeStyle(width, height, this.allowList)}
                  />
                </div>
              </Grid.Column>
            </Grid>

            {summaryErrors.length > 0 && (
              <Message negative>
                <Message.Header>エラー ({summaryErrors.length})</Message.Header>
                <Message.List items={summaryErrors} />
              </Message>
            )}
            {warnings.length > 0 && (
              <Message warning>
                <Message.Header>警告 ({warnings.length})</Message.Header>
                <Message.List items={warnings} />
              </Message>
            )}

            <details style={{ marginTop: 12 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  padding: '6px 0',
                  userSelect: 'none',
                }}
              >
                <span role="img" aria-label="設定">
                  ⚙
                </span>{' '}
                詳細設定 (サイズ・サンドボックス)
              </summary>
              <div style={{ marginTop: 8 }}>
                <Form.Group>
                  <Form.Field width={8} error={!!fieldErrors.width}>
                    <label htmlFor="html-embed-width">幅 (Width)</label>
                    <Input
                      id="html-embed-width"
                      type="text"
                      inputMode="numeric"
                      value={width}
                      placeholder={`自動 (100%) / ${this.allowList.minWidth}〜${this.allowList.maxWidth}px`}
                      onChange={this.handleSizeChange('width')}
                      error={!!fieldErrors.width}
                    />
                    <FieldError message={fieldErrors.width} />
                  </Form.Field>
                  <Form.Field width={8} error={!!fieldErrors.height}>
                    <label htmlFor="html-embed-height">高さ (Height)</label>
                    <Input
                      id="html-embed-height"
                      type="text"
                      inputMode="numeric"
                      value={height}
                      placeholder={`自動 / ${this.allowList.minHeight}〜${this.allowList.maxHeight}px`}
                      onChange={this.handleSizeChange('height')}
                      error={!!fieldErrors.height}
                    />
                    <FieldError message={fieldErrors.height} />
                  </Form.Field>
                </Form.Group>
                <Form.Field>
                  <label>サンドボックス</label>
                  {SANDBOX_TOKENS.map(token => (
                    <SandboxCheckbox
                      key={token}
                      token={token}
                      required={token === REQUIRED_SANDBOX_TOKEN}
                      checked={sandbox.has(token)}
                      onChange={this.handleSandboxToggle(token)}
                    />
                  ))}
                </Form.Field>
              </div>
            </details>
          </Form>

          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', padding: '6px 0', userSelect: 'none' }}>
              <span role="img" aria-label="ガイド">
                📖
              </span>{' '}
              {getGuideSummary(guideLang)}
            </summary>
            <div style={{ display: 'flex', gap: 6, margin: '8px 0', fontSize: 12 }}>
              {SUPPORTED_LANGS.map(lang => (
                <Button
                  key={lang}
                  size="mini"
                  basic={lang !== guideLang}
                  primary={lang === guideLang}
                  type="button"
                  onClick={this.handleGuideLangChange(lang)}
                >
                  {LANG_LABELS[lang]}
                </Button>
              ))}
            </div>
            <HtmlEmbedGuide lang={guideLang} />
          </details>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>キャンセル</Button>
          <Button primary disabled={!submitEnabled} onClick={this.handleInsert}>
            {initialHtml ? '更新' : '挿入'}
          </Button>
        </Modal.Actions>
      </Modal>
    )
  }
}

HtmlEmbedDialog.propTypes = propTypes
HtmlEmbedDialog.defaultProps = defaultProps

export default HtmlEmbedDialog
